import { useState, useEffect, useCallback } from 'react';
import { Developer, DeveloperFilters } from '../types/developer';
import { fetchDevelopers } from '../services/airtableService';
import { clearCache } from '../services/airtableService';

interface UseDevelopersReturn {
  developers: Developer[];
  allDevelopers: Developer[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  setFilters: React.Dispatch<React.SetStateAction<DeveloperFilters>>;
  applyFiltersAndSearch: () => void;
  clearCache: () => void;
  dataSource: 'airtable';
}

export const useDevelopers = (): UseDevelopersReturn => {
  const [developers, setDevelopers] = useState<Developer[]>([]);
  const [allDevelopers, setAllDevelopers] = useState<Developer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<DeveloperFilters>({
    skills: [],
    experienceLevel: [],
    hourlyRateRange: {
      min: 10,
      max: 100
    },
    availability: []
  });
  const [dataSource, setDataSource] = useState<'airtable'>('airtable');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const airtableData = await fetchDevelopers();
      
      if (airtableData.length > 0) {
        // Only use Airtable data
        const developersWithSource: Developer[] = airtableData.map((dev: Developer) => ({ 
          ...dev, 
          dataSource: 'airtable' as const 
        }));
        
        setAllDevelopers(developersWithSource);
        setDevelopers(developersWithSource);
        setDataSource('airtable');
        console.log(`Loaded ${airtableData.length} developers from Airtable`);
      } else {
        setError('No developers found in Airtable');
        setAllDevelopers([]);
        setDevelopers([]);
      }
      
    } catch (err) {
      console.error('Error fetching developers from Airtable:', err);
      setError('Failed to connect to Airtable. Please check your configuration.');
      setAllDevelopers([]);
      setDevelopers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Combined filter and search
  const applyFiltersAndSearch = useCallback(() => {
    let filtered = allDevelopers;
    // Apply filters
    filtered = filtered.filter((developer: Developer) => {
      // Skills
      if (filters.skills && filters.skills.length > 0) {
        const hasRequiredSkills = filters.skills.some((skill: string) => developer.skills.includes(skill));
        if (!hasRequiredSkills) return false;
      }
      // Experience
      if (filters.experienceLevel && filters.experienceLevel.length > 0) {
        if (!filters.experienceLevel.includes(developer.experienceLevel)) return false;
      }
      // Hourly rate
      if (filters.hourlyRateRange) {
        const { min, max } = filters.hourlyRateRange;
        if (developer.hourlyRate < min || developer.hourlyRate > max) return false;
      }
      // Availability
      if (filters.availability && filters.availability.length > 0) {
        if (!filters.availability.includes(developer.availability)) return false;
      }
      return true;
    });
    // Apply search
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((developer: Developer) =>
        developer.name.toLowerCase().includes(searchLower) ||
        developer.skills.some((skill: string) => skill.toLowerCase().includes(searchLower)) ||
        developer.bio.toLowerCase().includes(searchLower) ||
        developer.experienceLevel.toLowerCase().includes(searchLower)
      );
    }
    setDevelopers(filtered);
  }, [allDevelopers, filters, searchTerm]);

  // Run combined filter/search when filters or searchTerm change
  useEffect(() => {
    applyFiltersAndSearch();
  }, [applyFiltersAndSearch]);

  const handleClearCache = useCallback(() => {
    clearCache();
    fetchData(); // Refetch data after clearing cache
  }, [fetchData]);

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const unusedRefetch = refetch;

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Expose setSearchTerm and setFilters for UI
  return {
    developers,
    allDevelopers,
    loading,
    error,
    refetch: fetchData,
    setSearchTerm,
    setFilters,
    applyFiltersAndSearch,
    clearCache: handleClearCache,
    dataSource,
  };
}; 